import { CUSTOMER_CLASSES, toMinor } from '@ly360/core';
import { ALL_CHANNELS, Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Megaphone, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Field } from '../crm/forms';
import { api, download } from '../lib/api';
import { useAuth } from '../lib/auth';
import { NewCampaignDialog, type CampaignPreset } from './CampaignForm';
import {
  RETAIL_KEYS,
  useProducts,
  useSegments,
  useStores,
  type Reach,
  type Rule,
  type Segment,
} from './data';
import { ClassBadge, DialogButtons } from './ui';

const RULE_TYPES = [
  'class',
  'spent',
  'orders',
  'lastOrder',
  'bought',
  'replenishDue',
  'waitlist',
  'birthday',
  'store',
  'channel',
  'city',
  'stage',
  'tag',
] as const;
const FAMILIES = [
  'oud',
  'woody',
  'oriental',
  'floral',
  'fresh',
  'citrus',
  'fruity',
  'aromatic',
  'fougere',
  'gourmand',
];
const CATEGORIES = ['fragrance', 'bakhoor', 'oud_oil', 'gift_set', 'body_care'];

/** A rule in plain words ("Bought Creed or Kilian in the last 365 days"). */
export function useRuleText() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { session } = useAuth();
  const money = (n: unknown) =>
    fmt.money(toMinor(Number(n), session!.tenant.currency), session!.tenant.currency);
  const list = (xs: unknown) => (xs as string[]).join(', ');
  return (r: Rule): string => {
    const within = r.days ? ` ${t('retail.rule.inLast', { count: Number(r.days) })}` : '';
    switch (r.type) {
      case 'class':
        return t('retail.rule.text.class', {
          value: (r.classes as string[]).map((c) => t(`retail.class.${c}`)).join(', '),
        });
      case 'spent': {
        const key =
          r.min !== undefined && r.max !== undefined
            ? 'spent'
            : r.max !== undefined
              ? 'spentMax'
              : 'spentMin';
        return `${t(`retail.rule.text.${key}`, { min: money(r.min ?? 0), max: money(r.max ?? 0) })}${within}`;
      }
      case 'orders':
        return `${
          r.min !== undefined && r.max !== undefined
            ? t('retail.rule.text.orders', { min: r.min, max: r.max })
            : r.max !== undefined
              ? t('retail.rule.text.ordersMax', { count: Number(r.max) })
              : t('retail.rule.text.ordersMin', { count: Number(r.min ?? 0) })
        }${within}`;
      case 'lastOrder':
        return r.withinDays
          ? t('retail.rule.text.lastWithin', { count: Number(r.withinDays) })
          : t('retail.rule.text.lastOlder', { count: Number(r.olderThanDays ?? 0) });
      case 'bought': {
        const parts = [
          r.brands && list(r.brands),
          r.families && (r.families as string[]).map((f) => t(`retail.family.${f}`)).join(', '),
          r.categories && (r.categories as string[]).map((c) => t(`retail.category.${c}`)).join(', '),
          r.genders && (r.genders as string[]).map((g) => t(`retail.gender.${g}`)).join(', '),
        ].filter(Boolean);
        return `${t('retail.rule.text.bought', { value: parts.join(' · ') || t('retail.rule.anything') })}${within}`;
      }
      case 'birthday':
        return t('retail.rule.text.birthday', { count: Number(r.withinDays) });
      case 'channel':
        return t('retail.rule.text.channel', {
          value: (r.channels as string[]).map((c) => t(`channels.${c}`)).join(', '),
        });
      case 'city':
        return t('retail.rule.text.city', { value: list(r.cities) });
      case 'tag':
        return t('retail.rule.text.tag', { value: list(r.tags) });
      case 'stage':
        return t('retail.rule.text.stage', {
          value: (r.stages as string[]).map((s) => t(`crm.lifecycle.${s}`)).join(', '),
        });
      case 'store':
        return t('retail.rule.text.store', { count: (r.storeIds as string[]).length });
      case 'asked':
        return t(r.notBought === false ? 'retail.rule.text.asked' : 'retail.rule.text.askedNotBought', {
          value: list(r.words),
          count: Number(r.days ?? 30),
        });
      default:
        return t(`retail.rule.types.${r.type}`);
    }
  };
}

function ReachLine({ reach }: { reach: Reach }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (
    <span className="reach-line" data-testid="reach">
      <strong>{t('retail.segment.people', { count: reach.total })}</strong>
      <span className="faint">
        {t('retail.segment.reachable', {
          whatsapp: fmt.number(reach.whatsapp),
          email: fmt.number(reach.email),
        })}
      </span>
    </span>
  );
}

export function SegmentsPage() {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const qc = useQueryClient();
  const segments = useSegments();
  const ruleText = useRuleText();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<Segment | 'new' | null>(null);
  const [campaign, setCampaign] = useState<CampaignPreset | null>(null);
  const manage = can('campaigns:manage');
  const ar = i18n.language === 'ar';
  useEffect(() => {
    const id = params.get('open');
    const s = id && segments.data?.items.find((x) => x.id === id);
    if (s) {
      setEditing(s);
      setParams({}, { replace: true });
    }
  }, [params, segments.data, setParams]);
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/segments/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['segments'] }),
  });

  return (
    <>
      <PageHead
        eyebrow={t('nav.marketing')}
        title={t('retail.segments.title')}
        subtitle={t('retail.segments.subtitle')}
        actions={
          manage && (
            <button className="btn btn-grad" onClick={() => setEditing('new')} data-testid="add-segment">
              <Plus size={16} />
              {t('retail.segments.new')}
            </button>
          )
        }
      />
      {segments.isLoading && <Loading />}
      {segments.isError && <ErrorState error={segments.error} />}
      {segments.data && !segments.data.items.length && <Empty text={t('retail.segments.empty')} />}
      <div className="segment-grid" data-testid="segments">
        {segments.data?.items.map((s) => (
          <section key={s.id} className="card card-pad segment-card" data-testid="segment-card">
            <div className="segment-head">
              <h2 dir="auto">{(ar && s.nameAr) || s.name}</h2>
              {manage && (
                <button className="icon-btn" onClick={() => setEditing(s)} aria-label={t('common.edit')}>
                  <Pencil size={15} />
                </button>
              )}
            </div>
            {s.description && (
              <p className="faint" style={{ margin: 0 }} dir="auto">
                {s.description}
              </p>
            )}
            <ul className="rule-chips">
              {s.rules.all.map((r, i) => (
                <li key={i} className="chip">
                  {ruleText(r)}
                </li>
              ))}
            </ul>
            <ReachLine reach={s.reach} />
            <div className="segment-actions">
              {manage && (
                <button
                  className="btn btn-sm btn-grad"
                  onClick={() => setCampaign({ name: (ar && s.nameAr) || s.name, segmentId: s.id })}
                >
                  <Megaphone size={14} />
                  {t('retail.segments.campaign')}
                </button>
              )}
              {manage && (
                <button
                  className="btn btn-sm"
                  onClick={() =>
                    void download(`/v1/segments/${s.id}/audience.csv`, `audience-${s.id.slice(0, 8)}.csv`)
                  }
                  title={t('retail.segments.exportHint')}
                >
                  <Download size={14} />
                  {t('retail.segments.export')}
                </button>
              )}
              {manage && !s.campaigns && (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => remove.mutate(s.id)}
                  aria-label={t('common.delete')}
                >
                  <Trash2 size={14} />
                </button>
              )}
              {s.campaigns > 0 && (
                <Link className="faint" to="/campaigns" style={{ fontSize: 'var(--t-xs)' }}>
                  {t('retail.segments.usedBy', { count: s.campaigns })}
                </Link>
              )}
            </div>
          </section>
        ))}
      </div>
      {editing && (
        <SegmentDialog
          segment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            for (const k of RETAIL_KEYS) void qc.invalidateQueries({ queryKey: [k] });
            setEditing(null);
          }}
        />
      )}
      {campaign && <NewCampaignDialog open preset={campaign} onClose={() => setCampaign(null)} />}
    </>
  );
}

function blank(type: string): Rule {
  switch (type) {
    case 'class':
      return { type, classes: ['vip'] };
    case 'spent':
      return { type, min: 500 };
    case 'orders':
      return { type, min: 2 };
    case 'lastOrder':
      return { type, olderThanDays: 120 };
    case 'bought':
      return { type, families: ['oud'], days: 365 };
    case 'birthday':
      return { type, withinDays: 14 };
    case 'channel':
      return { type, channels: ['instagram'] };
    case 'city':
      return { type, cities: ['Doha'] };
    case 'tag':
      return { type, tags: ['vip'] };
    case 'stage':
      return { type, stages: ['customer'] };
    case 'store':
      return { type, storeIds: [] };
    default:
      return { type };
  }
}

function SegmentDialog({
  segment,
  onClose,
  onSaved,
}: {
  segment: Segment | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { session } = useAuth();
  const [name, setName] = useState(segment?.name ?? '');
  const [nameAr, setNameAr] = useState(segment?.nameAr ?? '');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [rules, setRules] = useState<Rule[]>(segment?.rules.all ?? [{ type: 'class', classes: ['vip'] }]);
  const [adding, setAdding] = useState('');
  const [debounced, setDebounced] = useState(rules);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(rules), 300);
    return () => clearTimeout(h);
  }, [rules]);
  // Lists that must not be empty (an empty list would match nobody).
  const required: Record<string, string> = {
    class: 'classes',
    channel: 'channels',
    stage: 'stages',
    store: 'storeIds',
    city: 'cities',
    tag: 'tags',
  };
  const valid = rules.every(
    (r) => !required[r.type] || ((r[required[r.type]!] as string[]) ?? []).length > 0,
  );
  const preview = useQuery({
    queryKey: ['segments', 'preview', debounced],
    enabled: valid,
    queryFn: () =>
      api<{
        reach: Reach;
        sample: {
          id: string;
          displayName: string;
          nameArabic: string | null;
          class: string;
          spentMinor: string;
        }[];
      }>('/v1/segments/preview', { method: 'POST', body: JSON.stringify({ rules: { all: debounced } }) }),
  });
  const save = useMutation({
    mutationFn: () =>
      api(segment ? `/v1/segments/${segment.id}` : '/v1/segments', {
        method: segment ? 'PATCH' : 'POST',
        body: JSON.stringify({
          name,
          nameAr: nameAr || null,
          description: description || null,
          rules: { all: rules },
        }),
      }),
    onSuccess: onSaved,
  });
  const update = (i: number, patch: Partial<Rule>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? ({ ...r, ...patch } as Rule) : r)));
  const ar = i18n.language === 'ar';

  return (
    <Dialog
      open
      wide
      title={segment ? t('retail.segments.edit') : t('retail.segments.new')}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      testId="segment-dialog"
      footer={
        <DialogButtons onClose={onClose} busy={save.isPending} disabled={!valid} label={t('common.save')} />
      }
    >
      <div className="form-grid">
        <Field id="sg-name" label={t('retail.segments.name')}>
          <input
            id="sg-name"
            className="input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field id="sg-name-ar" label={t('retail.segments.nameAr')}>
          <input
            id="sg-name-ar"
            className="input"
            dir="rtl"
            lang="ar"
            value={nameAr}
            onChange={(e) => setNameAr(e.target.value)}
          />
        </Field>
      </div>
      <Field id="sg-desc" label={t('retail.segments.description')}>
        <input
          id="sg-desc"
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <h3 className="section-title" style={{ marginTop: '1rem' }}>
        {t('retail.segments.rules')}
      </h3>
      <p className="faint" style={{ marginTop: 0 }}>
        {t('retail.segments.rulesHint')}
      </p>
      <ol className="rule-editor" data-testid="rule-editor">
        {rules.map((r, i) => (
          <li key={i} className="rule-edit">
            <div className="rule-edit-head">
              <strong>{t(`retail.rule.types.${r.type}`)}</strong>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}
                aria-label={t('retail.segments.removeRule')}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <RuleFields rule={r} onChange={(p) => update(i, p)} />
          </li>
        ))}
      </ol>
      <div className="rule-add">
        <Select
          id="sg-add"
          value={adding}
          onChange={(v) => {
            if (!v) return;
            setRules((rs) => [...rs, blank(v)]);
            setAdding('');
          }}
          placeholder={t('retail.segments.addRule')}
          aria-label={t('retail.segments.addRule')}
          data-testid="add-rule"
          options={RULE_TYPES.map((x) => ({
            value: x,
            label: t(`retail.rule.types.${x}`),
            hint: t(`retail.rule.hints.${x}`),
          }))}
        />
      </div>
      <div className="segment-preview" aria-live="polite">
        {preview.data && (
          <>
            <ReachLine reach={preview.data.reach} />
            <ul className="list-plain sample-list">
              {preview.data.sample.map((p) => (
                <li key={p.id}>
                  <span className="grow" dir="auto">
                    {(ar && p.nameArabic) || p.displayName}
                  </span>
                  <ClassBadge value={p.class} />
                  <span className="faint num">
                    {fmt.money(Number(p.spentMinor), session!.tenant.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        {preview.isError && <ErrorState error={preview.error} />}
      </div>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

function Chips({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <fieldset className="chip-set">
      <legend className="sr-only">{legend}</legend>
      {options.map((o) => (
        <label key={o.value} className="chip chip-toggle">
          <input
            type="checkbox"
            checked={value.includes(o.value)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, o.value] : value.filter((x) => x !== o.value))
            }
          />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: unknown;
  onChange: (v: number | undefined) => void;
  min?: number;
}) {
  return (
    <label className="rule-num">
      <span>{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        inputMode="numeric"
        value={value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    </label>
  );
}

function RuleFields({ rule: r, onChange }: { rule: Rule; onChange: (p: Partial<Rule>) => void }) {
  const { t, i18n } = useTranslation();
  const needsProducts = r.type === 'bought' || r.type === 'waitlist';
  const products = useProducts(needsProducts);
  const stores = useStores();
  const ar = i18n.language === 'ar';
  const words = (v: unknown) => ((v as string[] | undefined) ?? []).join(', ');
  const toWords = (s: string) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  const days = (
    <NumberInput
      label={t('retail.rule.days')}
      value={r.days}
      min={1}
      onChange={(v) => onChange({ days: v })}
    />
  );
  switch (r.type) {
    case 'class':
      return (
        <Chips
          legend={t('retail.rule.types.class')}
          value={(r.classes as string[]) ?? []}
          onChange={(v) => onChange({ classes: v })}
          options={CUSTOMER_CLASSES.map((c) => ({ value: c, label: t(`retail.class.${c}`) }))}
        />
      );
    case 'spent':
    case 'orders':
      return (
        <div className="rule-nums">
          <NumberInput
            label={t('retail.rule.atLeast')}
            value={r.min}
            onChange={(v) => onChange({ min: v })}
          />
          <NumberInput label={t('retail.rule.atMost')} value={r.max} onChange={(v) => onChange({ max: v })} />
          {days}
        </div>
      );
    case 'lastOrder':
      return (
        <div className="rule-nums">
          <NumberInput
            label={t('retail.rule.withinDays')}
            value={r.withinDays}
            min={1}
            onChange={(v) => onChange({ withinDays: v })}
          />
          <NumberInput
            label={t('retail.rule.olderThanDays')}
            value={r.olderThanDays}
            min={1}
            onChange={(v) => onChange({ olderThanDays: v })}
          />
        </div>
      );
    case 'bought':
      return (
        <div className="rule-stack">
          <Chips
            legend={t('retail.rule.families')}
            value={(r.families as string[]) ?? []}
            onChange={(v) => onChange({ families: v.length ? v : undefined })}
            options={FAMILIES.map((f) => ({ value: f, label: t(`retail.family.${f}`) }))}
          />
          <Chips
            legend={t('retail.rule.categories')}
            value={(r.categories as string[]) ?? []}
            onChange={(v) => onChange({ categories: v.length ? v : undefined })}
            options={CATEGORIES.map((c) => ({ value: c, label: t(`retail.category.${c}`) }))}
          />
          {products.data && (
            <Chips
              legend={t('retail.rule.brands')}
              value={(r.brands as string[]) ?? []}
              onChange={(v) => onChange({ brands: v.length ? v : undefined })}
              options={products.data.brands.map((b) => ({ value: b, label: b }))}
            />
          )}
          <div className="rule-nums">{days}</div>
        </div>
      );
    case 'waitlist':
      return products.data ? (
        <Chips
          legend={t('retail.rule.products')}
          value={(r.productIds as string[]) ?? []}
          onChange={(v) => onChange({ productIds: v.length ? v : undefined })}
          options={products.data.items
            .filter((p) => p.waiting > 0)
            .map((p) => ({ value: p.id, label: `${p.brand} ${p.name}` }))}
        />
      ) : null;
    case 'birthday':
      return (
        <div className="rule-nums">
          <NumberInput
            label={t('retail.rule.withinDays')}
            value={r.withinDays}
            onChange={(v) => onChange({ withinDays: v ?? 0 })}
          />
        </div>
      );
    case 'store':
      return (
        <Chips
          legend={t('retail.rule.types.store')}
          value={(r.storeIds as string[]) ?? []}
          onChange={(v) => onChange({ storeIds: v })}
          options={(stores.data?.items ?? []).map((s) => ({
            value: s.id,
            label: (ar && s.nameAr) || s.name,
          }))}
        />
      );
    case 'channel':
      return (
        <Chips
          legend={t('retail.rule.types.channel')}
          value={(r.channels as string[]) ?? []}
          onChange={(v) => onChange({ channels: v })}
          options={ALL_CHANNELS.map((c) => ({ value: c, label: t(`channels.${c}`) }))}
        />
      );
    case 'stage':
      return (
        <Chips
          legend={t('retail.rule.types.stage')}
          value={(r.stages as string[]) ?? []}
          onChange={(v) => onChange({ stages: v })}
          options={(['lead', 'contact', 'customer', 'churned'] as const).map((s) => ({
            value: s,
            label: t(`crm.lifecycle.${s}`),
          }))}
        />
      );
    case 'city':
    case 'tag': {
      const key = r.type === 'city' ? 'cities' : 'tags';
      return (
        <input
          className="input"
          aria-label={t(`retail.rule.types.${r.type}`)}
          defaultValue={words(r[key])}
          onBlur={(e) => onChange({ [key]: toWords(e.target.value) })}
          placeholder={t('retail.rule.commaSeparated')}
        />
      );
    }
    default:
      return (
        <p className="faint" style={{ margin: 0 }}>
          {t(`retail.rule.hints.${r.type}`)}
        </p>
      );
  }
}
